/**
 * Meta + JSON-LD injector for SEO landing pages.
 *
 * The site's global <Helmet> at the App.js root takes precedence over nested
 * <Helmet> blocks for `<title>` / `<meta>` (a well-known limitation when the
 * parent Helmet re-renders after the child mounts). We therefore manage SEO
 * head injection directly via DOM mutation in useEffect — the same approach
 * used by the existing /services/*-london pages, which are confirmed to
 * surface their meta tags correctly in production.
 *
 * On unmount we strip every node we added (tagged with data-seo-managed) so
 * leaving the page restores the global defaults.
 */
import { useEffect } from "react";
import { jsonLd } from "../../lib/schema";

const MANAGED_ATTR = "data-seo-managed";

function setOrCreateMeta(selector, attrName, attrValue, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setOrCreateLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function appendJsonLd(obj) {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute(MANAGED_ATTR, "true");
  script.textContent = jsonLd(obj);
  document.head.appendChild(script);
}

export function SeoHelmet({ title, description, canonical, schemas = [] }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    setOrCreateMeta('meta[name="description"]', "name", "description", description);
    setOrCreateMeta('meta[property="og:title"]', "property", "og:title", title);
    setOrCreateMeta('meta[property="og:description"]', "property", "og:description", description);
    setOrCreateMeta('meta[property="og:type"]', "property", "og:type", "website");
    setOrCreateMeta('meta[property="og:url"]', "property", "og:url", canonical);
    setOrCreateMeta('meta[name="robots"]', "name", "robots", "index,follow");
    setOrCreateLink("canonical", canonical);

    // Re-apply title after any later renders (defensive against root Helmet).
    const tick = setTimeout(() => { document.title = title; }, 50);

    schemas.forEach((schema) => appendJsonLd(schema));

    return () => {
      clearTimeout(tick);
      document.title = prevTitle;
      // Strip everything we added (meta + canonical + JSON-LD).
      document.head
        .querySelectorAll(`[${MANAGED_ATTR}="true"]`)
        .forEach((n) => n.remove());
    };
  }, [title, description, canonical, schemas]);

  return null;
}
